import { Component, OnInit, OnDestroy, Inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MenuItem, MenuItemGroupImage, ModifierGroup, ModifierGroups, Modifier, ModifierOption, EditModifierOptions } from '../../models/order';
import { Store } from 'src/app/models/store';
import { OrderService } from 'src/app/order.service';
import { QuantityChange } from '../quantity-control/quantity-control.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnalyticsService } from 'src/app/_services/analytics.service';

export interface MenuItemBottomSheetData {
  storeId: string;
  orderId: string;
  menuItemGroupId: number;
  store: Store;
  isCatering: boolean;
  // Edit mode (optional)
  parsedMenuItemId?: string;
  menuItemId?: string;
  /** Item photos from the menu list, newest first. Fetched if omitted. */
  images?: MenuItemGroupImage[];
  /** Group-level description from the menu list; shown when the portion has none of its own. */
  description?: string;
  /** 'modal' = centred desktop card (close icon), 'sheet' = full-screen mobile sheet (back arrow). */
  presentation?: 'modal' | 'sheet';
}

@Component({
  selector: 'menu-item-bottom-sheet',
  templateUrl: './menu-item-bottom-sheet.component.html',
  styleUrls: ['./menu-item-bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuItemBottomSheetComponent implements OnInit, OnDestroy {

  title: string;
  description: string;
  /** Menu-group description (what the card shows); the portion's own text wins when present. */
  private groupDescription = '';
  selectedMenuItem: MenuItem;
  menuGroupItems: MenuItem[];
  modifierGroups: ModifierGroup[];
  selectedModifiers: Modifier[];
  quantity: number = 1;
  specialRequest: string;
  isLoading = true;
  isSubmitting = false;
  selectedItemTotal: number = 0;
  selectedModifiersTotal: number = 0;
  isValid: boolean = false;
  availableQty: number;
  uiSetting: any;
  images: MenuItemGroupImage[] = [];
  currentImageIndex = 0;

  get store(): Store {
    return this.data.store;
  }

  get isModal(): boolean {
    return this.data.presentation === 'modal';
  }

  get currentImage(): MenuItemGroupImage | undefined {
    return this.images[this.currentImageIndex];
  }

  /** Detail view wants the 1200px `medium`; older rows only have the bounded original. */
  detailSrc(img: MenuItemGroupImage): string {
    return img?.medium || img?.image || img?.thumbnail;
  }

  selectImage(index: number) {
    if (index < 0 || index >= this.images.length) return;
    this.currentImageIndex = index;
    this.cdr.markForCheck();
  }

  prevImage() {
    this.selectImage(this.currentImageIndex === 0 ? this.images.length - 1 : this.currentImageIndex - 1);
  }

  nextImage() {
    this.selectImage(this.currentImageIndex === this.images.length - 1 ? 0 : this.currentImageIndex + 1);
  }

  private loadImages() {
    // The menu list hands us its photos (possibly none). Trust that — an empty array
    // means "this item has no photo", not "go and check", so photo-less items don't
    // cost an extra request every time they are opened.
    if (this.data.description) this.groupDescription = this.data.description;
    if (this.data.images !== undefined) {
      this.images = (this.data.images || []).filter(img => img && (img.medium || img.image || img.thumbnail));
      this.fitModalToPhotos();
      return;
    }
    // Edit flow / deep link: the list wasn't in front of us, ask the group endpoint.
    this.orderService.getMenuGroup(this.data.storeId, this.data.menuItemGroupId).subscribe(group => {
      const groupDesc = (group as any)?.description;
      if (groupDesc && groupDesc != 0 && !this.groupDescription) {
        this.groupDescription = String(groupDesc);
        if (!this.description) this.description = this.groupDescription;
      }
      this.images = (group?.latest_image || []).filter(img => img && (img.medium || img.image));
      this.currentImageIndex = 0;
      this.fitModalToPhotos();
      this.cdr.markForCheck();
    }, () => { this.fitModalToPhotos(); });
  }

  /** Desktop modal: two columns when there is a photo, one narrow column when there is none. */
  private fitModalToPhotos() {
    if (!this.isModal) return;
    this.dialogRef.updateSize(this.images.length ? '920px' : '600px');
  }

  constructor(
    private dialogRef: MatDialogRef<MenuItemBottomSheetComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MenuItemBottomSheetData,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    @Inject(DOCUMENT) private document: Document,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit() {
    this.document.body.style.overflow = 'hidden';
    this.uiSetting = this.store.ui_settings;
    this.loadImages();
    this.loadMenuItems();
  }

  ngOnDestroy() {
    this.document.body.style.overflow = '';
  }

  private loadMenuItems() {
    const { storeId, menuItemGroupId, isCatering, menuItemId } = this.data;

    this.orderService.getMenuItemGroupOptionsForStore(storeId, menuItemGroupId.toString(), isCatering)
      .subscribe(data => {
        this.menuGroupItems = data.filter(m => !m.is_pos_only);

        if (isCatering) {
          this.menuGroupItems = this.menuGroupItems.filter(item => item.show_on_catering_menu !== false);
        }

        if (this.menuGroupItems.length === 0) {
          this.dialogRef.close();
          return;
        }

        // If editing, select the specific item; otherwise first available
        if (menuItemId) {
          this.selectedMenuItem = this.menuGroupItems.find(p => p.id === +menuItemId);
        } else {
          this.selectedMenuItem = this.menuGroupItems.filter(m => m.is_available)[0];
        }

        if (this.selectedMenuItem) {
          this.availableQty = this.selectedMenuItem.available_qty;
          this.loadModifiers();
        } else {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private loadModifiers() {
    const { storeId, parsedMenuItemId, orderId } = this.data;

    this.orderService.getModifiersForMenuItem(storeId, this.selectedMenuItem.id.toString()).subscribe(data => {
      this.title = data.menu_item.display_name;
      this.description = data.menu_item.description || this.groupDescription;
      this.modifierGroups = data.modifier_groups
        .sort((x, y) => Number(y.is_required) - Number(x.is_required))
        .map(p => Object.assign(new ModifierGroup(), p));
      this.selectedItemTotal = this.selectedMenuItem.base_price;

      // Only on first open — getPortionChange() reloads modifiers and must not re-fire this.
      this.analyticsService.trackEvent('view_item', {
        item_id: this.selectedMenuItem.id,
        item_name: this.title,
        price: this.selectedMenuItem.base_price,
        store_id: storeId
      });

      if (parsedMenuItemId) {
        // Edit mode: load saved modifiers
        this.orderService.getModifiers(orderId, parsedMenuItemId).subscribe(editData => {
          this.specialRequest = editData.parsed_menu_item.special_request;
          this.quantity = editData.parsed_menu_item.quantity;

          let modifierOptions = Object.assign(new EditModifierOptions(), editData);
          let existing_modifiers = modifierOptions.parsed_menu_item.modifiers.filter(m => m.modifier != null);
          this.selectedModifiers = existing_modifiers.map(m => Object.assign(new ModifierOption(), m));
          this.selectedModifiersTotal = +this.selectedModifiers.reduce((sum, mod) => sum + +mod.getUpcharge(), 0);

          if (this.modifierGroups) {
            this.selectedModifiers.forEach(selectedMod => {
              const modifierGroupId = selectedMod.modifier.modifier_group;
              const modifierId = selectedMod.modifier.id;
              const modifierGroup = this.modifierGroups.find(group => group.id === modifierGroupId);
              if (modifierGroup) {
                modifierGroup.options.forEach(option => {
                  if (option.id === modifierId) {
                    option.mod_type = selectedMod.mod_type;
                  }
                });
              }
            });
          }

          this.rehydrateNestedSelections();
          this.isLoading = false;
          this.cdr.markForCheck();
        });
      } else {
        // New item: apply preselected modifiers if enabled
        const isPreselectEnabled = this.store['feature_flag']?.['preselected_default_pos_mod'];
        if (isPreselectEnabled) {
          const preselectedMods: Modifier[] = [];
          this.modifierGroups.forEach(group => {
            group.options.forEach(option => {
              if (option['preselect_default']) {
                const exists = preselectedMods.find(m => m.modifier.id === option.id);
                if (!exists) {
                  const mod = new Modifier();
                  mod.modifier = option;
                  mod.quantity = option['preselect_quantity'];
                  mod.mod_type = 'ADD';
                  preselectedMods.push(mod);
                }
              }
            });
          });
          this.selectedModifiers = preselectedMods;
          this.selectedModifiersTotal = +this.selectedModifiers.reduce((sum, mod) => sum + +mod.getUpcharge(), 0);
        } else {
          this.selectedModifiers = [];
          this.selectedModifiersTotal = 0;
        }
        this.isLoading = false;
        this.cdr.markForCheck();
      }
      this.cdr.markForCheck();
    });
  }

  private rehydrateNestedSelections(): void {
    if (!this.store?.isEnabled('sub_modifiers_enabled') || !this.modifierGroups || !this.selectedModifiers) {
      return;
    }
    const subGroupsByOption = new Map<number, ModifierGroup[]>();
    const parentByChildOption = new Map<number, number>();
    const walk = (groups: ModifierGroup[], parentOptionId?: number) => {
      for (const g of groups || []) {
        for (const o of g.options || []) {
          if (parentOptionId != null) {
            parentByChildOption.set(o.id, parentOptionId);
          }
          const subs = o.sub_modifier_groups;
          if (Array.isArray(subs) && subs.length) {
            subGroupsByOption.set(o.id, subs);
            walk(subs, o.id);
          }
        }
      }
    };
    walk(this.modifierGroups);

    const selectedIds = new Set(this.selectedModifiers.map(m => m.modifier?.id));
    this.selectedModifiers.forEach(m => {
      const optId = m.modifier?.id;
      if (optId == null) return;
      const subs = subGroupsByOption.get(optId);
      if (subs) {
        (m.modifier as ModifierOption).sub_modifier_groups = subs;
      }
      const parentId = parentByChildOption.get(optId);
      if (parentId != null && selectedIds.has(parentId)) {
        m.parentModifierId = parentId;
      }
    });
  }

  getPortionChange(value: MenuItem) {
    this.isLoading = true;
    this.selectedMenuItem = value;
    this.selectedModifiers = [];
    this.selectedModifiersTotal = 0;
    this.orderService.getModifiersForMenuItem(this.data.storeId, this.selectedMenuItem.id.toString()).subscribe(data => {
      this.title = data.menu_item.display_name;
      this.description = data.menu_item.description || this.groupDescription;
      this.modifierGroups = data.modifier_groups
        .sort((x, y) => Number(y.is_required) - Number(x.is_required))
        .map(p => Object.assign(new ModifierGroups(), p));
      this.isLoading = false;

      const isPreselectEnabled = this.store['feature_flag']?.['preselected_default_pos_mod'];
      if (isPreselectEnabled) {
        const preselectedMods: Modifier[] = [];
        this.modifierGroups.forEach(group => {
          group.options.forEach(option => {
            if (option['preselect_default']) {
              const exists = preselectedMods.find(m => m.modifier.id === option.id);
              if (!exists) {
                const mod = new Modifier();
                mod.modifier = option;
                mod.quantity = option['preselect_quantity'];
                mod.mod_type = 'ADD';
                preselectedMods.push(mod);
              }
            }
          });
        });
        this.selectedModifiers = preselectedMods;
        this.getSelectedModifiers(this.selectedModifiers);
      }

      this.selectedItemTotal = this.selectedMenuItem.base_price;
      this.cdr.markForCheck();
    });
  }

  getSelectedModifiers(values: Modifier[]) {
    this.selectedModifiersTotal = 0;
    this.selectedModifiers = values;
    this.selectedModifiers.forEach(m => {
      if (m.mod_tag === 'No') return;

      if (m.modifier.half_mod_upcharge == null) {
        let upcharge = m.mod_type === 'H1' || m.mod_type === 'H2' ? +m.getUpcharge() / 2.0 : +m.getUpcharge();
        this.selectedModifiersTotal = +this.selectedModifiersTotal + upcharge;
      } else {
        if (m.mod_type === 'H1' || m.mod_type === 'H2') {
          if (m.modifier.half_mod_upcharge == null) {
            let upcharge = +m.getUpcharge() / 2.0;
            this.selectedModifiersTotal = +this.selectedModifiersTotal + upcharge;
          } else {
            let upcharge = m.getUpcharge();
            this.selectedModifiersTotal = +this.selectedModifiersTotal + upcharge;
          }
        } else {
          let upcharge = +m.getUpcharge();
          this.selectedModifiersTotal = +this.selectedModifiersTotal + upcharge;
        }
      }

      if (m.modifier.mod_tag === 'No' && m.modifier.no_tag_upcharge) {
        this.selectedModifiersTotal = +this.selectedModifiersTotal + m.modifier.no_tag_upcharge;
      } else if (m.modifier.mod_tag === 'Lite' && m.modifier.lite_tag_upcharge) {
        this.selectedModifiersTotal = +this.selectedModifiersTotal + m.modifier.lite_tag_upcharge;
      } else if (m.modifier.mod_tag === 'Extra' && m.modifier.extra_tag_upcharge) {
        this.selectedModifiersTotal = +this.selectedModifiersTotal + m.modifier.extra_tag_upcharge;
      } else if (m.modifier.mod_tag === 'On Side' && m.modifier.onside_tag_upcharge) {
        this.selectedModifiersTotal = +this.selectedModifiersTotal + m.modifier.onside_tag_upcharge;
      } else if (m.modifier.mod_tag === 'Sub' && m.modifier.sub_tag_upcharge) {
        this.selectedModifiersTotal = +this.selectedModifiersTotal + m.modifier.sub_tag_upcharge;
      }
    });
  }

  getQuantity(value: QuantityChange) {
    this.quantity = value.quantity;
  }

  getSpecialRequest(value: string) {
    this.specialRequest = value;
  }

  checkModifierConstrains(value) {
    this.isValid = value;
  }

  addToCart() {
    if (!this.selectedMenuItem || this.isSubmitting) return;

    if (!this.data.orderId) {
      this.snackBar.open('Order session not found. Please refresh.', 'OK', { verticalPosition: 'top' });
      return;
    }

    this.isSubmitting = true;
    this.cdr.markForCheck();

    if (this.data.parsedMenuItemId) {
      // Edit mode
      this.selectedModifiers.forEach(mod => {
        if (mod.mod_tag && mod.modifier) {
          mod.modifier.mod_tag = mod.mod_tag;
        }
      });
      this.orderService.updateItem(
        this.data.orderId, this.data.parsedMenuItemId,
        this.selectedMenuItem.id.toString(),
        this.selectedModifiers, this.quantity, this.specialRequest
      ).subscribe(
        () => this.dialogRef.close({ success: true }),
        () => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
          this.snackBar.open('Failed to update item. Please try again.', 'OK', { verticalPosition: 'top' });
        }
      );
    } else {
      // New item
      this.analyticsService.trackEvent('add_to_cart', {
        item_id: this.selectedMenuItem.id,
        item_name: this.title,
        price: this.selectedMenuItem.base_price,
        quantity: this.quantity,
        store_id: this.data.storeId
      });
      this.orderService.addnewItem(
        this.data.orderId, this.selectedMenuItem.id.toString(),
        this.selectedModifiers, this.quantity, this.specialRequest
      ).subscribe(
        () => this.dialogRef.close({ success: true }),
        () => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
          this.snackBar.open('Failed to add item. Please try again.', 'OK', { verticalPosition: 'top' });
        }
      );
    }
  }

  close(event?: Event) {
    if (event) event.preventDefault();
    this.dialogRef.close();
  }
}
